import { forwardRef } from 'react';
import { cx } from '@/utils';
import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Typography variants
 * @param {string} props.className - HTML class
 * @param {string} props.variant - Typography variant style
 * All sizes are based on a base of 16px
 * H1: 40px 3xl:44px
 * H2: 32px 3xl:36px
 * H3: 28px 3xl:32px
 * H4: 24px 3xl:28px
 * H5: 20px 3xl:24px
 * H6: 18px 3xl:20px
 * Body1: 16px 3xl:18px
 * Body2: 14px 3xl:16px
 * Small: 12px 3xl:14px
 */
const TypographyVariants = cva('font-montserrat font-semibold not-italic text-primary', {
  variants: {
    variant: {
      h1: '!text-xl  md:!text-[2.5rem] md:!leading-[48px] 3xl:!text-[2.75rem] 3xl:!leading-[52px] max-md:!text-2xl max-md:!leading-8',
      h2: '!text-lg  md:!text-[2rem] md:!leading-[38.4px] 3xl:!text-[2.25rem] 3xl:!leading-[42.4px] max-md:!text-xl max-md:!leading-7',
      h3: '!text-base  md:!text-[1.75rem] md:!leading-[33.6px] 3xl:!text-[2rem] 3xl:!leading-[38.4px] max-md:!text-lg max-md:!leading-6',
      h4: '!text-sm  md:!text-2xl 3xl:!text-[1.75rem] 3xl:!leading-[33.6px] max-md:!text-base max-md:!leading-5',
      h5: '!text-sm md:!text-xl 3xl:!text-2xl max-md:!text-sm max-md:!leading-5',
      h6: '!text-sm  md:!text-lg 3xl:!text-xl max-md:!text-sm max-md:!leading-5',
      body1:
        'font-workSans !text-base font-normal not-italic text-inherit 3xl:!text-lg max-md:!text-sm',
      body2:
        'font-workSans !text-sm font-normal not-italic text-inherit 3xl:!text-base max-md:!text-xs',
      small: 'font-workSans !text-xs font-normal not-italic text-inherit 3xl:!text-sm',
    },
  },
  defaultVariants: {
    variant: 'body1',
  },
});
export type TypographyProps = {
  as?: React.ElementType;
} & React.HTMLAttributes<HTMLElement> &
  VariantProps<typeof TypographyVariants>;

/**
 * Typography component
 * @param {React.HTMLAttributes<HTMLElement>} props - HTML attributes
 * @param {React.ElementType} [props.as] - HTML tag to render
 * @param {string} [props.variant] - Typography variant style
 * @returns {React.ReactElement} - React component of type props.as or props.variant or 'p'
 */
const Typography = forwardRef<HTMLElement, TypographyProps>(
  ({ className, variant, as, ...props }, ref) => {
    function getElementType() {
      if (as) return as;
      if (variant && variant !== 'body2' && variant !== 'body1') return variant;
      return 'p';
    }

    const Tag = getElementType();
    return <Tag className={cx(TypographyVariants({ variant, className }))} ref={ref} {...props} />;
  }
);
Typography.displayName = 'Typography';

export { Typography, TypographyVariants };
